import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowButton,
  ArrowGroup,
  CarouselHeader,
  CarouselTitle,
  CarouselTrack,
  CarouselWrapper,
  ProgressBarInner,
  ProgressBarOuter,
  ProgressTrack,
} from './Carousel.styles';
import type { CarouselProps } from './Carousel.types';

const ChevronLeft = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="10 4 6 8 10 12" />
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 4 10 8 6 12" />
  </svg>
);

export const Carousel = ({
  children,
  title,
  visibleCount,
  scrollAmount,
  showProgress = false,
}: CarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollLeft(track.scrollLeft > 0);
    setCanScrollRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 1);

    const maxScroll = track.scrollWidth - track.clientWidth;
    setScrollProgress(maxScroll > 0 ? track.scrollLeft / maxScroll : 0);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateArrows();

    track.addEventListener('scroll', updateArrows, { passive: true });

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateArrows) : null;
    observer?.observe(track);

    return () => {
      track.removeEventListener('scroll', updateArrows);
      observer?.disconnect();
    };
  }, [updateArrows]);

  const getScrollAmount = () => {
    if (scrollAmount) return scrollAmount;
    const track = trackRef.current;
    if (track) return track.clientWidth;
    return 300;
  };

  const scrollLeft = () => {
    trackRef.current?.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
  };

  const scrollRight = () => {
    trackRef.current?.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
  };

  const safeVisibleCount = visibleCount && visibleCount > 0 ? visibleCount : undefined;

  const trackStyle = safeVisibleCount
    ? ({
        '--carousel-child-width': `calc((100% - ${(safeVisibleCount - 1) * 16}px) / ${safeVisibleCount})`,
      } as React.CSSProperties)
    : undefined;

  const arrows = (
    <ArrowGroup>
      <ArrowButton
        type="button"
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        aria-label="Scroll left"
      >
        <ChevronLeft />
      </ArrowButton>
      <ArrowButton
        type="button"
        onClick={scrollRight}
        disabled={!canScrollRight}
        aria-label="Scroll right"
      >
        <ChevronRight />
      </ArrowButton>
    </ArrowGroup>
  );

  return (
    <CarouselWrapper>
      {(title || canScrollLeft || canScrollRight) && (
        <CarouselHeader>
          {title ? <CarouselTitle>{title}</CarouselTitle> : <span />}
          {arrows}
        </CarouselHeader>
      )}
      <CarouselTrack ref={trackRef} style={trackStyle} data-testid="carousel-track">
        {children}
      </CarouselTrack>
      {showProgress && (canScrollLeft || canScrollRight) && (
        <ProgressTrack>
          <ProgressBarOuter>
            <ProgressBarInner
              data-testid="carousel-progress"
              style={{ width: '40%', left: `${scrollProgress * 60}%` }}
            />
          </ProgressBarOuter>
        </ProgressTrack>
      )}
    </CarouselWrapper>
  );
};
